import {
  collection,
  doc,
  endAt,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  startAt,
  where,
} from 'firebase/firestore'

import { db } from 'utils/firebase'
import { toSlug } from 'utils/string'

import { Data, MultiItemQuery, Options } from 'types/query'
import { UserType } from 'types/user'

import { formatDocSnapshot, formatQuerySnapshot } from './formatFirestoreResults'
import { formatQueryOptions } from './formatQueryOptions'

export const fetchUsers = async (
  data: Data,
  options: Options<any> = {},
): Promise<Array<UserType>> => {
  const { type } = data

  if (!type) throw new Error('type is required')

  const baseQuery = query(
    collection(db, 'users'),
    where('type', '==', type),
    where('firstName', '!=', null),
  )
  const queryWithOptions = formatQueryOptions(baseQuery, options)

  const querySnapshot = await getDocs(queryWithOptions)
  const formattedQuery = formatQuerySnapshot(querySnapshot)
  return formattedQuery.filter((item) => item.username)
}

export const fetchCoaches = async (options: Options<any>): Promise<Partial<UserType>[]> => {
  const baseQuery = query(collection(db, 'users'), where('type', '==', 'trainer'))
  const queryWithOptions = formatQueryOptions(baseQuery, options)
  const querySnapshot = await getDocs(queryWithOptions)
  return formatQuerySnapshot(querySnapshot)
}

export const fetchOrCreateUser = async (data: { user: UserType }): Promise<UserType> => {
  const { user } = data
  if (!user?.id) throw new Error('No data passed into fetchUser')

  const userRef = doc(db, 'users', user.id)
  let docSnap = await getDoc(userRef)

  if (!docSnap.exists()) {
    await postUser({
      ...data,
      updatedOrCreatedData: {
        id: user.id,
      },
    })
    docSnap = await getDoc(userRef)
  }

  return formatDocSnapshot(docSnap)
}

export const fetchUser = async (data: Data) => {
  if (data.username) {
    return fetchUserByUsername(data)
  }
  return fetchUserByUserId(data)
}

export const fetchUserByUserId = async (data: Data): Promise<UserType | null> => {
  if (!data || !data?.userId) {
    throw new Error('No data passed into fetchUserByUserId')
  }

  const { userId } = data
  const userRef = doc(db, 'users', userId)
  const docSnap = await getDoc(userRef)
  if (!docSnap.exists()) throw new Error('User not found in fetchUserByUserId')
  return formatDocSnapshot(docSnap)
}

export const fetchUserByUsername = async (data: Data): Promise<UserType | null> => {
  if (!data || !data?.username) {
    throw new Error('No data passed into fetchUserByUsername')
  }
  const { username } = data

  const q = query(collection(db, 'users'), where('username', '==', username))
  const querySnapshot = await getDocs(q)
  const results = formatQuerySnapshot(querySnapshot)
  if (results.length === 0) {
    console.error('User not found in fetchUserByUsername')
    return null
  }

  return results[0]
}

/**
 * Retrieves each User that matches each username provided.
 * @param usernames A string array of usernames to fetch.
 * @returns a Promise of an array of Users that match the usernames provided
 */
export const fetchUsersByUsernames = async (usernames: string[]) => {
  const userPromises = usernames.map((username) => fetchUserByUsername({ username }))
  const users = Promise.all(userPromises)
  return users
}

/**
 * Retrieves each User that matches each ID provided.
 * @param userIds A string array of user IDs to fetch.
 * @returns a Promise of an array of Users that match the IDs provided
 */
export const fetchUsersByIds = async (userIds: string[]) => {
  const userPromises = userIds.map((id) => fetchUserByUserId({ userId: id }))
  const users = Promise.all(userPromises)
  return users
}

export const postUser = async (data: Data) => {
  if (!data || !data?.userId || !data?.updatedOrCreatedData)
    throw new Error('No data passed into postUser')

  const { userId, updatedOrCreatedData } = data
  const userRef = doc(db, 'users', userId)

  if (updatedOrCreatedData.username) {
    const slugifiedUsername = toSlug(updatedOrCreatedData.username)
    updatedOrCreatedData.username = slugifiedUsername
    /*
      If the user is updating their username, we need to check its availability
    */
    let existingUser
    try {
      existingUser = await fetchUserByUsername({
        username: updatedOrCreatedData.username,
      })
    } catch (err) {
      existingUser = null
    }

    if (existingUser && existingUser?.id !== userId) {
      throw new Error('The username is already taken')
    }
  }

  const fullData = { ...updatedOrCreatedData, updated: new Date() }
  await setDoc(userRef, fullData, { merge: true })
  return fullData
}

export const fetchUsersSearch: MultiItemQuery<{ searchTerm: string }, UserType> = async (
  { searchTerm },
  options = {},
) => {
  const baseQuery = query(
    collection(db, 'users'),
    where('firstName', '!=', null),
    orderBy('firstName', 'asc'),
    startAt(searchTerm),
    endAt(`${searchTerm}\uf8ff`),
  )
  const queryWithOptions = formatQueryOptions(baseQuery, options)

  const querySnapshot = await getDocs(queryWithOptions)
  const formattedQuery = formatQuerySnapshot(querySnapshot)
  return formattedQuery
}
